import { Dialog, DialogProps, SxProps, Theme } from '@mui/material';

import { useIsMobile } from '@/hooks/use-is-mobile';

export interface ResponsiveDialogProps extends DialogProps {
  /**
   * Go full-screen below `sm` (default). Pass `false` for short prompts
   * (confirm/delete/message) — they stay a centred card, just with tighter
   * side margins so they never exceed the viewport.
   */
  fullScreenOnMobile?: boolean;
}

/** Applied last so the full-screen paper drops the desktop card chrome. */
const mobileSx: SxProps<Theme> = theme => ({
  '& .MuiDialog-paperFullScreen': {
    borderRadius: 0,
    maxWidth: '100%',
    maxHeight: '100%',
    width: '100%',
    height: '100%',
    m: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper:not(.MuiDialog-paperFullScreen)': {
      m: 2,
      maxWidth: 'calc(100% - 32px)',
      width: 'calc(100% - 32px)',
    },
  },
});

/**
 * Drop-in replacement for MUI `Dialog`: centred modal on desktop, full-screen
 * on phones. Title/actions stay pinned while `DialogContent` scrolls (MUI's
 * default `scroll='paper'`). All Dialog props pass through unchanged.
 */
const ResponsiveDialog = ({
  fullScreenOnMobile = true,
  fullScreen,
  sx,
  ...props
}: ResponsiveDialogProps) => {
  const isMobile = useIsMobile();
  const userSx = Array.isArray(sx) ? sx : [sx];

  return (
    <Dialog
      fullScreen={fullScreen ?? (fullScreenOnMobile && isMobile)}
      sx={[...userSx, mobileSx]}
      {...props}
    />
  );
};

export default ResponsiveDialog;
